/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import type { SectionListItem } from '@airasia-phoenix/react-native/components/SearchableListPageView';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import type { UseStationListDataParams } from '../types';
import { searchStations } from '../utils/stationUtils';
import {
  buildRecentItems,
  buildStationSearchItems,
} from '../utils/stationSearchListUtils';

export const useStationListData = ({
  query,
  stations,
  searchIndex,
  recentStations,
  validDestinationCodes,
  idPrefix,
  t,
}: UseStationListDataParams): SectionListItem[] | MemoizedListViewProps[] => {
  const searchResults = useMemo(() => {
    if (!query || query.length < 1) return [];
    const results =
      searchIndex?.search(query) ?? searchStations(stations, query);
    let filtered = results.filter((station) => station.AAFlight === true);
    if (validDestinationCodes?.length) {
      filtered = filtered.filter((station) =>
        validDestinationCodes.includes(station.StationCode),
      );
    }
    return filtered;
  }, [query, searchIndex, stations, validDestinationCodes]);

  const filteredRecentStations = useMemo(() => {
    if (!validDestinationCodes?.length) return recentStations;
    return recentStations.filter((item) =>
      validDestinationCodes.includes(item.station.StationCode),
    );
  }, [recentStations, validDestinationCodes]);

  const recentItems = useMemo(
    () => buildRecentItems(filteredRecentStations, t, idPrefix),
    [filteredRecentStations, t, idPrefix],
  );

  return useMemo(() => {
    if (query.length > 0) {
      return buildStationSearchItems(searchResults, query, t, idPrefix);
    }
    if (recentItems.length > 0) {
      return [
        {
          title: t('searchSnapPage.recentSearch.title'),
          data: recentItems,
        },
      ];
    }
    return [];
  }, [query, searchResults, recentItems, t, idPrefix]);
};
